<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ThemeSettingsAPI from 'dashboard/api/themeSettings';
import WebsiteLayout from '../components/WebsiteLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ThemeColorPicker from '../components/ThemeColorPicker.vue';
import ImageUploader from '../components/ImageUploader.vue';

const { t } = useI18n();

const loading = ref(false);
const saving = ref(false);
const activeSection = ref('branding');

const themeData = ref({
  site_name: '',
  site_tagline: '',
  logo_url: '',
  favicon_url: '',
  colors: {},
  typography: {},
  layout: {},
  components: {},
  header: {},
  footer: {},
  custom_css: '',
  active: true,
});

const headerTitle = computed(() => t('WEBSITE.HEADER.THEME_TITLE'));

const tailwindConfig = ref('');
const loadingTailwind = ref(false);

// Liste des Google Fonts populaires
const googleFonts = [
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Roboto, sans-serif', label: 'Roboto' },
  { value: 'Open Sans, sans-serif', label: 'Open Sans' },
  { value: 'Lato, sans-serif', label: 'Lato' },
  { value: 'Montserrat, sans-serif', label: 'Montserrat' },
  { value: 'Poppins, sans-serif', label: 'Poppins' },
  { value: 'Raleway, sans-serif', label: 'Raleway' },
  { value: 'Nunito, sans-serif', label: 'Nunito' },
  { value: 'Playfair Display, serif', label: 'Playfair Display' },
  { value: 'Merriweather, serif', label: 'Merriweather' },
  { value: 'PT Sans, sans-serif', label: 'PT Sans' },
  { value: 'Source Sans Pro, sans-serif', label: 'Source Sans Pro' },
  { value: 'Oswald, sans-serif', label: 'Oswald' },
  { value: 'Quicksand, sans-serif', label: 'Quicksand' },
  { value: 'Ubuntu, sans-serif', label: 'Ubuntu' },
  { value: 'Rubik, sans-serif', label: 'Rubik' },
  { value: 'Work Sans, sans-serif', label: 'Work Sans' },
  { value: 'Noto Sans, sans-serif', label: 'Noto Sans' },
  { value: 'Fira Sans, sans-serif', label: 'Fira Sans' },
  { value: 'DM Sans, sans-serif', label: 'DM Sans' },
];

const sections = [
  {
    key: 'branding',
    label: t('WEBSITE.THEME.BRANDING.TITLE'),
    icon: 'i-lucide-sparkles',
  },
  {
    key: 'colors',
    label: t('WEBSITE.THEME.COLORS.TITLE'),
    icon: 'i-lucide-palette',
  },
  {
    key: 'typography',
    label: t('WEBSITE.THEME.TYPOGRAPHY.TITLE'),
    icon: 'i-lucide-type',
  },
  {
    key: 'layout',
    label: t('WEBSITE.THEME.LAYOUT.TITLE'),
    icon: 'i-lucide-layout',
  },
  {
    key: 'components',
    label: t('WEBSITE.THEME.COMPONENTS.TITLE'),
    icon: 'i-lucide-box',
  },
  {
    key: 'header',
    label: t('WEBSITE.THEME.HEADER.TITLE'),
    icon: 'i-lucide-panel-top',
  },
  {
    key: 'footer',
    label: t('WEBSITE.THEME.FOOTER.TITLE'),
    icon: 'i-lucide-panel-bottom',
  },
  {
    key: 'tailwind',
    label: t('WEBSITE.THEME.TAILWIND.TITLE'),
    icon: 'i-lucide-wind',
  },
  {
    key: 'custom',
    label: t('WEBSITE.THEME.CUSTOM_CSS.TITLE'),
    icon: 'i-lucide-code',
  },
  {
    key: 'prompt',
    label: t('WEBSITE.THEME.PROMPT.TITLE'),
    icon: 'i-lucide-file-down',
  },
];

const fetchTheme = async () => {
  loading.value = true;
  try {
    const response = await ThemeSettingsAPI.get();
    themeData.value = response.data;
    // Initialiser les objets footer s'ils n'existent pas
    if (!themeData.value.footer) {
      themeData.value.footer = {};
    }
    if (!themeData.value.footer.social_links) {
      themeData.value.footer.social_links = {};
    }
    if (!themeData.value.footer.quick_links) {
      themeData.value.footer.quick_links = { title: 'Quick Links', links: [] };
    }
    if (!themeData.value.footer.quick_links.links) {
      themeData.value.footer.quick_links.links = [];
    }
    if (!themeData.value.footer.services) {
      themeData.value.footer.services = { title: 'Our Services', items: [] };
    }
    if (!themeData.value.footer.services.items) {
      themeData.value.footer.services.items = [];
    }
    if (!themeData.value.footer.contact) {
      themeData.value.footer.contact = {};
    }
    if (!themeData.value.footer.bottom) {
      themeData.value.footer.bottom = {};
    }
    if (!themeData.value.footer.bottom.legal_links) {
      themeData.value.footer.bottom.legal_links = {};
    }
  } catch (error) {
    useAlert(t('WEBSITE.THEME.API.ERROR.FETCH_FAILED'));
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchTheme();
});

const fetchTailwindConfig = async () => {
  loadingTailwind.value = true;
  try {
    const response = await ThemeSettingsAPI.get();
    const accountId = response.config.url.match(/accounts\/(\d+)/)[1];
    const configResponse = await fetch(
      `/api/v1/accounts/${accountId}/tailwind_config`
    );
    const data = await configResponse.json();
    tailwindConfig.value = data.content;
  } catch (error) {
    useAlert(t('WEBSITE.THEME.API.ERROR.FETCH_FAILED'));
  } finally {
    loadingTailwind.value = false;
  }
};

const handleSave = async () => {
  saving.value = true;
  try {
    await ThemeSettingsAPI.update({ theme_setting: themeData.value });
    useAlert(t('WEBSITE.THEME.API.SUCCESS.UPDATED'));
  } catch (error) {
    useAlert(t('WEBSITE.THEME.API.ERROR.UPDATE_FAILED'));
  } finally {
    saving.value = false;
  }
};

const generateThemePrompt = () => {
  const data = themeData.value;
  const codeFence = '```';

  return `# Configuration du Thème - ${data.site_name || 'Mon Site'}

## Identité de Marque

- **Nom du site** : ${data.site_name || 'Non défini'}
- **Slogan** : ${data.site_tagline || 'Non défini'}
- **Logo** : ${data.logo_url || 'Non défini'}
- **Favicon** : ${data.favicon_url || 'Non défini'}

## Palette de Couleurs

${codeFence}css
--color-primary: ${data.colors?.primary || '#3b82f6'};
--color-secondary: ${data.colors?.secondary || '#6366f1'};
--color-accent: ${data.colors?.accent || '#f59e0b'};
--color-background: ${data.colors?.background || '#ffffff'};
--color-surface: ${data.colors?.surface || '#f9fafb'};
--color-text: ${data.colors?.text || '#1f2937'};
--color-text-secondary: ${data.colors?.text_secondary || '#6b7280'};
--color-border: ${data.colors?.border || '#e5e7eb'};
--color-success: ${data.colors?.success || '#10b981'};
--color-warning: ${data.colors?.warning || '#f59e0b'};
--color-error: ${data.colors?.error || '#ef4444'};
--color-info: ${data.colors?.info || '#3b82f6'};
${codeFence}

## Typographie

- **Police principale** : ${data.typography?.font_family_primary || 'Inter, sans-serif'}
- **Police secondaire** : ${data.typography?.font_family_secondary || 'Inter, sans-serif'}
- **Taille de base** : ${data.typography?.font_size_base || '16px'}
- **Taille des titres** : ${data.typography?.font_size_heading || '2rem'}
- **Hauteur de ligne** : ${data.typography?.line_height_base || '1.5'}
- **Hauteur de ligne titres** : ${data.typography?.line_height_heading || '1.2'}

## Mise en Page

- **Largeur max du container** : ${data.layout?.container_max_width || '1280px'}
- **Border radius** : ${data.layout?.border_radius || '0.5rem'}
- **Espacement de base** : ${data.layout?.spacing_unit || '1rem'}
- **Hauteur header** : ${data.layout?.header_height || '64px'}
- **Hauteur footer** : ${data.layout?.footer_height || 'auto'}

## Composants

- **Style des boutons** : ${data.components?.button_style || 'rounded'}
- **Taille des boutons** : ${data.components?.button_size || 'medium'}
- **Ombre des cartes** : ${data.components?.card_shadow || 'medium'}
- **Style des inputs** : ${data.components?.input_style || 'outlined'}

## Header

- **Afficher le logo** : ${data.header?.show_logo ? 'Oui' : 'Non'}
- **Afficher le slogan** : ${data.header?.show_tagline ? 'Oui' : 'Non'}
- **Position** : ${data.header?.position || 'sticky'}
- **Fond** : ${data.header?.background || 'solid'}
- **Flou** : ${data.header?.blur ? 'Oui' : 'Non'}

## Footer

- **Afficher le footer** : ${data.footer?.show_footer ? 'Oui' : 'Non'}
- **Fond** : ${data.footer?.background || 'solid'}
- **Alignement** : ${data.footer?.text_align || 'center'}

## CSS Personnalisé

${data.custom_css ? codeFence + 'css\n' + data.custom_css + '\n' + codeFence : 'Aucun CSS personnalisé'}

---

*Généré le ${new Date().toLocaleString('fr-FR')}*
`;
};

const exportThemePrompt = () => {
  const markdown = generateThemePrompt();
  const blob = new Blob([markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `theme-config-${Date.now()}.md`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  useAlert('Fichier markdown exporté avec succès');
};

const copyThemePrompt = () => {
  const markdown = generateThemePrompt();
  navigator.clipboard.writeText(markdown);
  useAlert('Configuration copiée dans le presse-papier');
};

// Méthodes pour gérer les listes dynamiques du footer
const addQuickLink = () => {
  if (!themeData.value.footer.quick_links) {
    themeData.value.footer.quick_links = { title: 'Quick Links', links: [] };
  }
  if (!themeData.value.footer.quick_links.links) {
    themeData.value.footer.quick_links.links = [];
  }
  themeData.value.footer.quick_links.links.push({ label: '', url: '' });
};

const removeQuickLink = index => {
  if (
    themeData.value.footer.quick_links?.links &&
    Array.isArray(themeData.value.footer.quick_links.links)
  ) {
    themeData.value.footer.quick_links.links.splice(index, 1);
  }
};

const addService = () => {
  if (!themeData.value.footer.services) {
    themeData.value.footer.services = { title: 'Our Services', items: [] };
  }
  if (!themeData.value.footer.services.items) {
    themeData.value.footer.services.items = [];
  }
  themeData.value.footer.services.items.push('');
};

const removeService = index => {
  if (
    themeData.value.footer.services?.items &&
    Array.isArray(themeData.value.footer.services.items)
  ) {
    themeData.value.footer.services.items.splice(index, 1);
  }
};

const updateServiceItem = (index, value) => {
  if (!themeData.value.footer.services) {
    themeData.value.footer.services = { title: 'Our Services', items: [] };
  }
  if (!themeData.value.footer.services.items) {
    themeData.value.footer.services.items = [];
  }
  if (Array.isArray(themeData.value.footer.services.items)) {
    themeData.value.footer.services.items[index] = value;
  }
};

const updateQuickLinkLabel = (index, value) => {
  if (!themeData.value.footer.quick_links) {
    themeData.value.footer.quick_links = { title: 'Quick Links', links: [] };
  }
  if (!themeData.value.footer.quick_links.links) {
    themeData.value.footer.quick_links.links = [];
  }
  if (!themeData.value.footer.quick_links.links[index]) {
    themeData.value.footer.quick_links.links[index] = { label: '', url: '' };
  }
  themeData.value.footer.quick_links.links[index].label = value;
};

const updateQuickLinkUrl = (index, value) => {
  if (!themeData.value.footer.quick_links) {
    themeData.value.footer.quick_links = { title: 'Quick Links', links: [] };
  }
  if (!themeData.value.footer.quick_links.links) {
    themeData.value.footer.quick_links.links = [];
  }
  if (!themeData.value.footer.quick_links.links[index]) {
    themeData.value.footer.quick_links.links[index] = { label: '', url: '' };
  }
  themeData.value.footer.quick_links.links[index].url = value;
};

const updateSocialLink = (key, value) => {
  if (!themeData.value.footer.social_links) {
    themeData.value.footer.social_links = {};
  }
  themeData.value.footer.social_links[key] = value;
};

const updateQuickLinksTitle = value => {
  if (!themeData.value.footer.quick_links) {
    themeData.value.footer.quick_links = { title: 'Quick Links', links: [] };
  }
  themeData.value.footer.quick_links.title = value;
};

const updateServicesTitle = value => {
  if (!themeData.value.footer.services) {
    themeData.value.footer.services = { title: 'Our Services', items: [] };
  }
  themeData.value.footer.services.title = value;
};

const updateContactField = (key, value) => {
  if (!themeData.value.footer.contact) {
    themeData.value.footer.contact = {};
  }
  themeData.value.footer.contact[key] = value;
};

const updateBottomField = (key, value) => {
  if (!themeData.value.footer.bottom) {
    themeData.value.footer.bottom = {};
  }
  themeData.value.footer.bottom[key] = value;
};

const updateLegalLink = (key, value) => {
  if (!themeData.value.footer.bottom) {
    themeData.value.footer.bottom = {};
  }
  if (!themeData.value.footer.bottom.legal_links) {
    themeData.value.footer.bottom.legal_links = {};
  }
  themeData.value.footer.bottom.legal_links[key] = value;
};
</script>

<template>
  <WebsiteLayout :header-title="headerTitle">
    <template #actions>
      <Button
        :label="t('WEBSITE.THEME.SAVE')"
        icon="i-lucide-save"
        size="sm"
        :loading="saving"
        @click="handleSave"
      />
    </template>

    <div v-if="loading" class="flex items-center justify-center py-10">
      <Spinner />
    </div>

    <div v-else class="flex gap-6">
      <!-- Sidebar navigation -->
      <div class="w-64 flex-shrink-0">
        <div
          class="sticky top-4 bg-n-solid-2 border border-n-weak rounded-lg p-2"
        >
          <nav class="space-y-1">
            <button
              v-for="section in sections"
              :key="section.key"
              class="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all"
              :class="[
                activeSection === section.key
                  ? 'bg-n-blue-9 text-white shadow-sm'
                  : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12',
              ]"
              @click="activeSection = section.key"
            >
              <span class="size-[18px]" :class="[section.icon]" />
              <span class="flex-1 text-left">{{ section.label }}</span>
              <span
                v-if="activeSection === section.key"
                class="i-lucide-chevron-right size-4 opacity-80"
              />
            </button>
          </nav>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0">
        <!-- Branding -->
        <div v-show="activeSection === 'branding'" class="space-y-6">
          <div>
            <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
              {{ t('WEBSITE.THEME.BRANDING.TITLE') }}
            </h3>
            <div class="space-y-6">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.THEME.BRANDING.SITE_NAME') }}
                </label>
                <Input
                  v-model="themeData.site_name"
                  :placeholder="
                    t('WEBSITE.THEME.BRANDING.SITE_NAME_PLACEHOLDER')
                  "
                  class="w-full"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.THEME.BRANDING.SITE_TAGLINE') }}
                </label>
                <Input
                  v-model="themeData.site_tagline"
                  :placeholder="
                    t('WEBSITE.THEME.BRANDING.SITE_TAGLINE_PLACEHOLDER')
                  "
                  class="w-full"
                />
              </div>

              <div class="grid grid-cols-2 gap-6">
                <ImageUploader
                  v-model="themeData.logo_url"
                  :label="t('WEBSITE.THEME.BRANDING.LOGO')"
                  preview-size="md"
                />

                <ImageUploader
                  v-model="themeData.favicon_url"
                  :label="t('WEBSITE.THEME.BRANDING.FAVICON')"
                  preview-size="sm"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Colors -->
        <div v-show="activeSection === 'colors'" class="space-y-6">
          <div>
            <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
              {{ t('WEBSITE.THEME.COLORS.TITLE') }}
            </h3>
            <div class="grid grid-cols-2 gap-4">
              <ThemeColorPicker
                v-model="themeData.colors.primary"
                :label="t('WEBSITE.THEME.COLORS.PRIMARY')"
              />
              <ThemeColorPicker
                v-model="themeData.colors.secondary"
                :label="t('WEBSITE.THEME.COLORS.SECONDARY')"
              />
              <ThemeColorPicker
                v-model="themeData.colors.accent"
                :label="t('WEBSITE.THEME.COLORS.ACCENT')"
              />
              <ThemeColorPicker
                v-model="themeData.colors.background"
                :label="t('WEBSITE.THEME.COLORS.BACKGROUND')"
              />
              <ThemeColorPicker
                v-model="themeData.colors.surface"
                :label="t('WEBSITE.THEME.COLORS.SURFACE')"
              />
              <ThemeColorPicker
                v-model="themeData.colors.text"
                :label="t('WEBSITE.THEME.COLORS.TEXT')"
              />
              <ThemeColorPicker
                v-model="themeData.colors.text_secondary"
                :label="t('WEBSITE.THEME.COLORS.TEXT_SECONDARY')"
              />
              <ThemeColorPicker
                v-model="themeData.colors.border"
                :label="t('WEBSITE.THEME.COLORS.BORDER')"
              />
              <ThemeColorPicker
                v-model="themeData.colors.success"
                :label="t('WEBSITE.THEME.COLORS.SUCCESS')"
              />
              <ThemeColorPicker
                v-model="themeData.colors.warning"
                :label="t('WEBSITE.THEME.COLORS.WARNING')"
              />
              <ThemeColorPicker
                v-model="themeData.colors.error"
                :label="t('WEBSITE.THEME.COLORS.ERROR')"
              />
              <ThemeColorPicker
                v-model="themeData.colors.info"
                :label="t('WEBSITE.THEME.COLORS.INFO')"
              />
            </div>
          </div>
        </div>

        <!-- Typography -->
        <div v-show="activeSection === 'typography'" class="space-y-6">
          <div>
            <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
              {{ t('WEBSITE.THEME.TYPOGRAPHY.TITLE') }}
            </h3>
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.THEME.TYPOGRAPHY.FONT_PRIMARY') }}
                </label>
                <select
                  v-model="themeData.typography.font_family_primary"
                  class="w-full px-3 py-2.5 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
                  :style="{
                    fontFamily: themeData.typography.font_family_primary,
                  }"
                >
                  <option
                    v-for="font in googleFonts"
                    :key="font.value"
                    :value="font.value"
                    :style="{ fontFamily: font.value }"
                  >
                    {{ font.label }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.THEME.TYPOGRAPHY.FONT_SECONDARY') }}
                </label>
                <select
                  v-model="themeData.typography.font_family_secondary"
                  class="w-full px-3 py-2.5 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
                  :style="{
                    fontFamily: themeData.typography.font_family_secondary,
                  }"
                >
                  <option
                    v-for="font in googleFonts"
                    :key="font.value"
                    :value="font.value"
                    :style="{ fontFamily: font.value }"
                  >
                    {{ font.label }}
                  </option>
                </select>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.TYPOGRAPHY.FONT_SIZE_BASE') }}
                  </label>
                  <Input
                    v-model="themeData.typography.font_size_base"
                    placeholder="16px"
                    class="w-full"
                  />
                </div>

                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.TYPOGRAPHY.FONT_SIZE_HEADING') }}
                  </label>
                  <Input
                    v-model="themeData.typography.font_size_heading"
                    placeholder="2rem"
                    class="w-full"
                  />
                </div>

                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.TYPOGRAPHY.LINE_HEIGHT_BASE') }}
                  </label>
                  <Input
                    v-model="themeData.typography.line_height_base"
                    placeholder="1.5"
                    class="w-full"
                  />
                </div>

                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.TYPOGRAPHY.LINE_HEIGHT_HEADING') }}
                  </label>
                  <Input
                    v-model="themeData.typography.line_height_heading"
                    placeholder="1.2"
                    class="w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Layout -->
        <div v-show="activeSection === 'layout'" class="space-y-6">
          <div>
            <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
              {{ t('WEBSITE.THEME.LAYOUT.TITLE') }}
            </h3>
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.THEME.LAYOUT.CONTAINER_WIDTH') }}
                </label>
                <Input
                  v-model="themeData.layout.container_max_width"
                  placeholder="1280px"
                  class="w-full"
                />
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.LAYOUT.BORDER_RADIUS') }}
                  </label>
                  <Input
                    v-model="themeData.layout.border_radius"
                    placeholder="0.5rem"
                    class="w-full"
                  />
                </div>

                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.LAYOUT.SPACING') }}
                  </label>
                  <Input
                    v-model="themeData.layout.spacing_unit"
                    placeholder="1rem"
                    class="w-full"
                  />
                </div>

                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.LAYOUT.HEADER_HEIGHT') }}
                  </label>
                  <Input
                    v-model="themeData.layout.header_height"
                    placeholder="64px"
                    class="w-full"
                  />
                </div>

                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.LAYOUT.FOOTER_HEIGHT') }}
                  </label>
                  <Input
                    v-model="themeData.layout.footer_height"
                    placeholder="auto"
                    class="w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Components -->
        <div v-show="activeSection === 'components'" class="space-y-6">
          <div>
            <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
              {{ t('WEBSITE.THEME.COMPONENTS.TITLE') }}
            </h3>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.THEME.COMPONENTS.BUTTON_STYLE') }}
                </label>
                <select
                  v-model="themeData.components.button_style"
                  class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
                >
                  <option value="rounded">
                    {{ t('WEBSITE.THEME.COMPONENTS.STYLES.rounded') }}
                  </option>
                  <option value="square">
                    {{ t('WEBSITE.THEME.COMPONENTS.STYLES.square') }}
                  </option>
                  <option value="pill">
                    {{ t('WEBSITE.THEME.COMPONENTS.STYLES.pill') }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.THEME.COMPONENTS.BUTTON_SIZE') }}
                </label>
                <select
                  v-model="themeData.components.button_size"
                  class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
                >
                  <option value="small">
                    {{ t('WEBSITE.THEME.COMPONENTS.STYLES.small') }}
                  </option>
                  <option value="medium">
                    {{ t('WEBSITE.THEME.COMPONENTS.STYLES.medium') }}
                  </option>
                  <option value="large">
                    {{ t('WEBSITE.THEME.COMPONENTS.STYLES.large') }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.THEME.COMPONENTS.CARD_SHADOW') }}
                </label>
                <select
                  v-model="themeData.components.card_shadow"
                  class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
                >
                  <option value="none">
                    {{ t('WEBSITE.THEME.COMPONENTS.STYLES.none') }}
                  </option>
                  <option value="subtle">
                    {{ t('WEBSITE.THEME.COMPONENTS.STYLES.subtle') }}
                  </option>
                  <option value="medium">
                    {{ t('WEBSITE.THEME.COMPONENTS.STYLES.medium') }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.THEME.COMPONENTS.INPUT_STYLE') }}
                </label>
                <select
                  v-model="themeData.components.input_style"
                  class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
                >
                  <option value="outlined">
                    {{ t('WEBSITE.THEME.COMPONENTS.STYLES.outlined') }}
                  </option>
                  <option value="filled">
                    {{ t('WEBSITE.THEME.COMPONENTS.STYLES.filled') }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Header -->
        <div v-show="activeSection === 'header'" class="space-y-6">
          <div>
            <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
              {{ t('WEBSITE.THEME.HEADER.TITLE') }}
            </h3>
            <div class="space-y-4">
              <div class="flex items-center gap-4">
                <label class="flex items-center gap-2">
                  <Checkbox v-model="themeData.header.show_logo" />
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('WEBSITE.THEME.HEADER.SHOW_LOGO') }}
                  </span>
                </label>

                <label class="flex items-center gap-2">
                  <Checkbox v-model="themeData.header.show_tagline" />
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('WEBSITE.THEME.HEADER.SHOW_TAGLINE') }}
                  </span>
                </label>

                <label class="flex items-center gap-2">
                  <Checkbox v-model="themeData.header.blur" />
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('WEBSITE.THEME.HEADER.BLUR') }}
                  </span>
                </label>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.HEADER.POSITION') }}
                  </label>
                  <select
                    v-model="themeData.header.position"
                    class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
                  >
                    <option value="static">
                      {{ t('WEBSITE.THEME.HEADER.POSITIONS.static') }}
                    </option>
                    <option value="sticky">
                      {{ t('WEBSITE.THEME.HEADER.POSITIONS.sticky') }}
                    </option>
                    <option value="fixed">
                      {{ t('WEBSITE.THEME.HEADER.POSITIONS.fixed') }}
                    </option>
                  </select>
                </div>

                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.HEADER.BACKGROUND') }}
                  </label>
                  <select
                    v-model="themeData.header.background"
                    class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
                  >
                    <option value="solid">
                      {{ t('WEBSITE.THEME.HEADER.BACKGROUNDS.solid') }}
                    </option>
                    <option value="transparent">
                      {{ t('WEBSITE.THEME.HEADER.BACKGROUNDS.transparent') }}
                    </option>
                    <option value="gradient">
                      {{ t('WEBSITE.THEME.HEADER.BACKGROUNDS.gradient') }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div v-show="activeSection === 'footer'" class="space-y-6">
          <div>
            <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
              {{ t('WEBSITE.THEME.FOOTER.TITLE') }}
            </h3>
            <div class="space-y-6">
              <!-- Paramètres généraux -->
              <div class="space-y-4">
                <h4 class="text-md font-semibold text-n-slate-12">
                  {{ t('WEBSITE.THEME.FOOTER.GENERAL_SETTINGS') }}
                </h4>
                <div>
                  <label class="flex items-center gap-2">
                    <Checkbox v-model="themeData.footer.show_footer" />
                    <span class="text-sm font-medium text-n-slate-12">
                      {{ t('WEBSITE.THEME.FOOTER.SHOW_FOOTER') }}
                    </span>
                  </label>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      class="block text-sm font-medium text-n-slate-12 mb-1.5"
                    >
                      {{ t('WEBSITE.THEME.FOOTER.BACKGROUND') }}
                    </label>
                    <select
                      v-model="themeData.footer.background"
                      class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
                    >
                      <option value="solid">
                        {{ t('WEBSITE.THEME.HEADER.BACKGROUNDS.solid') }}
                      </option>
                      <option value="transparent">
                        {{ t('WEBSITE.THEME.HEADER.BACKGROUNDS.transparent') }}
                      </option>
                      <option value="gradient">
                        {{ t('WEBSITE.THEME.HEADER.BACKGROUNDS.gradient') }}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label
                      class="block text-sm font-medium text-n-slate-12 mb-1.5"
                    >
                      {{ t('WEBSITE.THEME.FOOTER.TEXT_ALIGN') }}
                    </label>
                    <select
                      v-model="themeData.footer.text_align"
                      class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
                    >
                      <option value="left">
                        {{ t('WEBSITE.THEME.FOOTER.ALIGNMENTS.left') }}
                      </option>
                      <option value="center">
                        {{ t('WEBSITE.THEME.FOOTER.ALIGNMENTS.center') }}
                      </option>
                      <option value="right">
                        {{ t('WEBSITE.THEME.FOOTER.ALIGNMENTS.right') }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Informations de l'entreprise -->
              <div class="space-y-4 pt-4 border-t border-n-weak">
                <h4 class="text-md font-semibold text-n-slate-12">
                  {{ t('WEBSITE.THEME.FOOTER.COMPANY_INFO.TITLE') }}
                </h4>
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.FOOTER.COMPANY_INFO.DESCRIPTION') }}
                  </label>
                  <Textarea
                    :model-value="themeData.footer.company_description || ''"
                    :placeholder="
                      t(
                        'WEBSITE.THEME.FOOTER.COMPANY_INFO.DESCRIPTION_PLACEHOLDER'
                      )
                    "
                    rows="3"
                    class="w-full"
                    @update:model-value="
                      themeData.footer.company_description = $event
                    "
                  />
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.FOOTER.COMPANY_INFO.TAGLINE') }}
                  </label>
                  <Input
                    :model-value="themeData.footer.company_tagline || ''"
                    :placeholder="
                      t('WEBSITE.THEME.FOOTER.COMPANY_INFO.TAGLINE_PLACEHOLDER')
                    "
                    class="w-full"
                    @update:model-value="
                      themeData.footer.company_tagline = $event
                    "
                  />
                </div>
                <div class="space-y-3">
                  <label class="block text-sm font-medium text-n-slate-12">
                    {{ t('WEBSITE.THEME.FOOTER.COMPANY_INFO.SOCIAL_LINKS') }}
                  </label>
                  <div class="grid grid-cols-3 gap-4">
                    <div>
                      <label class="block text-xs text-n-slate-11 mb-1">
                        LinkedIn
                      </label>
                      <Input
                        :model-value="
                          themeData.footer.social_links?.linkedin || ''
                        "
                        placeholder="https://linkedin.com/company/..."
                        class="w-full"
                        @update:model-value="
                          updateSocialLink('linkedin', $event)
                        "
                      />
                    </div>
                    <div>
                      <label class="block text-xs text-n-slate-11 mb-1">
                        Twitter
                      </label>
                      <Input
                        :model-value="
                          themeData.footer.social_links?.twitter || ''
                        "
                        placeholder="https://twitter.com/..."
                        class="w-full"
                        @update:model-value="
                          updateSocialLink('twitter', $event)
                        "
                      />
                    </div>
                    <div>
                      <label class="block text-xs text-n-slate-11 mb-1">
                        Facebook
                      </label>
                      <Input
                        :model-value="
                          themeData.footer.social_links?.facebook || ''
                        "
                        placeholder="https://facebook.com/..."
                        class="w-full"
                        @update:model-value="
                          updateSocialLink('facebook', $event)
                        "
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Liens rapides -->
              <div class="space-y-4 pt-4 border-t border-n-weak">
                <h4 class="text-md font-semibold text-n-slate-12">
                  {{ t('WEBSITE.THEME.FOOTER.QUICK_LINKS.TITLE') }}
                </h4>
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.FOOTER.QUICK_LINKS.HEADING') }}
                  </label>
                  <Input
                    :model-value="themeData.footer.quick_links?.title || ''"
                    :placeholder="
                      t('WEBSITE.THEME.FOOTER.QUICK_LINKS.HEADING_PLACEHOLDER')
                    "
                    class="w-full"
                    @update:model-value="updateQuickLinksTitle($event)"
                  />
                </div>
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-n-slate-12">
                    {{ t('WEBSITE.THEME.FOOTER.QUICK_LINKS.LINKS') }}
                  </label>
                  <div
                    v-for="(link, index) in themeData.footer.quick_links
                      ?.links || []"
                    :key="index"
                    class="flex gap-2 items-start"
                  >
                    <div class="flex-1 grid grid-cols-2 gap-2">
                      <Input
                        :model-value="link.label"
                        :placeholder="
                          t(
                            'WEBSITE.THEME.FOOTER.QUICK_LINKS.LINK_LABEL_PLACEHOLDER'
                          )
                        "
                        class="w-full"
                        @update:model-value="
                          updateQuickLinkLabel(index, $event)
                        "
                      />
                      <Input
                        :model-value="link.url"
                        :placeholder="
                          t(
                            'WEBSITE.THEME.FOOTER.QUICK_LINKS.LINK_URL_PLACEHOLDER'
                          )
                        "
                        class="w-full"
                        @update:model-value="updateQuickLinkUrl(index, $event)"
                      />
                    </div>
                    <Button
                      icon="i-lucide-trash-2"
                      size="sm"
                      color="slate"
                      variant="outline"
                      @click="removeQuickLink(index)"
                    />
                  </div>
                  <Button
                    :label="t('WEBSITE.THEME.FOOTER.QUICK_LINKS.ADD_LINK')"
                    icon="i-lucide-plus"
                    size="sm"
                    color="slate"
                    variant="outline"
                    @click="addQuickLink"
                  />
                </div>
              </div>

              <!-- Services -->
              <div class="space-y-4 pt-4 border-t border-n-weak">
                <h4 class="text-md font-semibold text-n-slate-12">
                  {{ t('WEBSITE.THEME.FOOTER.SERVICES.TITLE') }}
                </h4>
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.FOOTER.SERVICES.HEADING') }}
                  </label>
                  <Input
                    :model-value="themeData.footer.services?.title || ''"
                    :placeholder="
                      t('WEBSITE.THEME.FOOTER.SERVICES.HEADING_PLACEHOLDER')
                    "
                    class="w-full"
                    @update:model-value="updateServicesTitle($event)"
                  />
                </div>
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-n-slate-12">
                    {{ t('WEBSITE.THEME.FOOTER.SERVICES.ITEMS') }}
                  </label>
                  <div
                    v-for="(item, index) in themeData.footer.services?.items ||
                    []"
                    :key="index"
                    class="flex gap-2 items-start"
                  >
                    <Input
                      :model-value="item"
                      :placeholder="
                        t('WEBSITE.THEME.FOOTER.SERVICES.ITEM_PLACEHOLDER')
                      "
                      class="flex-1"
                      @update:model-value="updateServiceItem(index, $event)"
                    />
                    <Button
                      icon="i-lucide-trash-2"
                      size="sm"
                      color="slate"
                      variant="outline"
                      @click="removeService(index)"
                    />
                  </div>
                  <Button
                    :label="t('WEBSITE.THEME.FOOTER.SERVICES.ADD_ITEM')"
                    icon="i-lucide-plus"
                    size="sm"
                    color="slate"
                    variant="outline"
                    @click="addService"
                  />
                </div>
              </div>

              <!-- Contact -->
              <div class="space-y-4 pt-4 border-t border-n-weak">
                <h4 class="text-md font-semibold text-n-slate-12">
                  {{ t('WEBSITE.THEME.FOOTER.CONTACT.TITLE') }}
                </h4>
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.FOOTER.CONTACT.ADDRESS') }}
                  </label>
                  <Textarea
                    :model-value="themeData.footer.contact?.address || ''"
                    :placeholder="
                      t('WEBSITE.THEME.FOOTER.CONTACT.ADDRESS_PLACEHOLDER')
                    "
                    rows="2"
                    class="w-full"
                    @update:model-value="updateContactField('address', $event)"
                  />
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.FOOTER.CONTACT.PHONE') }}
                  </label>
                  <Input
                    :model-value="themeData.footer.contact?.phone || ''"
                    :placeholder="
                      t('WEBSITE.THEME.FOOTER.CONTACT.PHONE_PLACEHOLDER')
                    "
                    class="w-full"
                    @update:model-value="updateContactField('phone', $event)"
                  />
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.FOOTER.CONTACT.EMAIL') }}
                  </label>
                  <Input
                    :model-value="themeData.footer.contact?.email || ''"
                    type="email"
                    :placeholder="
                      t('WEBSITE.THEME.FOOTER.CONTACT.EMAIL_PLACEHOLDER')
                    "
                    class="w-full"
                    @update:model-value="updateContactField('email', $event)"
                  />
                </div>
              </div>

              <!-- Section du bas -->
              <div class="space-y-4 pt-4 border-t border-n-weak">
                <h4 class="text-md font-semibold text-n-slate-12">
                  {{ t('WEBSITE.THEME.FOOTER.BOTTOM.TITLE') }}
                </h4>
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.THEME.FOOTER.BOTTOM.COPYRIGHT') }}
                  </label>
                  <Input
                    :model-value="themeData.footer.bottom?.copyright || ''"
                    :placeholder="
                      t('WEBSITE.THEME.FOOTER.BOTTOM.COPYRIGHT_PLACEHOLDER')
                    "
                    class="w-full"
                    @update:model-value="updateBottomField('copyright', $event)"
                  />
                </div>
                <div class="space-y-3">
                  <label class="block text-sm font-medium text-n-slate-12">
                    {{ t('WEBSITE.THEME.FOOTER.BOTTOM.LEGAL_LINKS') }}
                  </label>
                  <div class="space-y-2">
                    <div>
                      <label class="block text-xs text-n-slate-11 mb-1">
                        {{ t('WEBSITE.THEME.FOOTER.BOTTOM.TERMS_OF_SERVICE') }}
                      </label>
                      <Input
                        :model-value="
                          themeData.footer.bottom?.legal_links
                            ?.terms_of_service || ''
                        "
                        :placeholder="
                          t('WEBSITE.THEME.FOOTER.BOTTOM.URL_PLACEHOLDER')
                        "
                        class="w-full"
                        @update:model-value="
                          updateLegalLink('terms_of_service', $event)
                        "
                      />
                    </div>
                    <div>
                      <label class="block text-xs text-n-slate-11 mb-1">
                        {{ t('WEBSITE.THEME.FOOTER.BOTTOM.TERMS_OF_USE') }}
                      </label>
                      <Input
                        :model-value="
                          themeData.footer.bottom?.legal_links?.terms_of_use ||
                          ''
                        "
                        :placeholder="
                          t('WEBSITE.THEME.FOOTER.BOTTOM.URL_PLACEHOLDER')
                        "
                        class="w-full"
                        @update:model-value="
                          updateLegalLink('terms_of_use', $event)
                        "
                      />
                    </div>
                    <div>
                      <label class="block text-xs text-n-slate-11 mb-1">
                        {{ t('WEBSITE.THEME.FOOTER.BOTTOM.PRIVACY_POLICY') }}
                      </label>
                      <Input
                        :model-value="
                          themeData.footer.bottom?.legal_links
                            ?.privacy_policy || ''
                        "
                        :placeholder="
                          t('WEBSITE.THEME.FOOTER.BOTTOM.URL_PLACEHOLDER')
                        "
                        class="w-full"
                        @update:model-value="
                          updateLegalLink('privacy_policy', $event)
                        "
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tailwind Config -->
        <div v-show="activeSection === 'tailwind'" class="space-y-6">
          <div>
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold text-n-slate-12">
                {{ t('WEBSITE.THEME.TAILWIND.TITLE') }}
              </h3>
              <Button
                v-if="!tailwindConfig"
                label="Charger"
                icon="i-lucide-download"
                size="sm"
                color="slate"
                variant="outline"
                @click="fetchTailwindConfig"
              />
            </div>
            <div
              class="p-4 bg-n-blue-2/30 border border-n-blue-6 rounded-lg mb-4"
            >
              <p class="text-sm text-n-blue-11">
                <span class="i-lucide-info size-4 inline-block mr-1" />
                {{ t('WEBSITE.THEME.TAILWIND.INFO') }}
              </p>
            </div>
            <div
              v-if="loadingTailwind"
              class="flex items-center justify-center py-10"
            >
              <Spinner />
            </div>
            <Textarea
              v-else
              v-model="tailwindConfig"
              :placeholder="t('WEBSITE.THEME.TAILWIND.PLACEHOLDER')"
              :rows="20"
              class="w-full font-mono text-xs"
              readonly
            />
          </div>
        </div>

        <!-- Custom CSS -->
        <div v-show="activeSection === 'custom'" class="space-y-6">
          <div>
            <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
              {{ t('WEBSITE.THEME.CUSTOM_CSS.TITLE') }}
            </h3>
            <Textarea
              v-model="themeData.custom_css"
              :placeholder="t('WEBSITE.THEME.CUSTOM_CSS.PLACEHOLDER')"
              :rows="20"
              class="w-full font-mono text-sm"
            />
          </div>
        </div>

        <!-- Export Prompt -->
        <div v-show="activeSection === 'prompt'" class="space-y-6">
          <div>
            <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
              {{ t('WEBSITE.THEME.PROMPT.TITLE') }}
            </h3>

            <div
              class="p-4 bg-n-purple-2/30 border border-n-purple-6 rounded-lg mb-6"
            >
              <p class="text-sm text-n-purple-11 mb-3">
                <span class="i-lucide-sparkles size-4 inline-block mr-1" />
                {{ t('WEBSITE.THEME.PROMPT.DESCRIPTION') }}
              </p>
              <div class="flex gap-3">
                <Button
                  :label="t('WEBSITE.THEME.PROMPT.EXPORT')"
                  icon="i-lucide-download"
                  size="sm"
                  @click="exportThemePrompt"
                />
                <Button
                  :label="t('WEBSITE.THEME.PROMPT.COPY')"
                  icon="i-lucide-copy"
                  size="sm"
                  color="slate"
                  variant="outline"
                  @click="copyThemePrompt"
                />
              </div>
            </div>

            <div class="border border-n-weak rounded-lg p-4 bg-n-solid-1">
              <h4 class="text-sm font-semibold text-n-slate-12 mb-3">
                {{ t('WEBSITE.THEME.PROMPT.PREVIEW') }}
              </h4>
              <pre
                class="text-xs font-mono text-n-slate-11 whitespace-pre-wrap max-h-[500px] overflow-y-auto"
              >
                {{ generateThemePrompt() }}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  </WebsiteLayout>
</template>
